import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Receipt, TrendingUp, Car, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { EVChargingCostsTab } from "@/components/EVChargingCostsTab";

interface EmployeeVehicleCostsTabProps {
  employeeId: string;
  employeeName?: string;
  readOnly?: boolean;
}

interface EVHomeCharging {
  id: string;
  calculated_amount: number | null;
  is_settled: boolean;
}

interface Vehicle {
  id: string;
  name: string;
  manufacturer: string | null;
  license_plate: string | null;
  leasing_rate: number;
  insurance_monthly: number;
  tax_yearly: number;
  fuel_monthly: number;
  responsible_employee_id: string | null;
}

interface VehicleOneTimeCost {
  id: string;
  vehicle_id: string;
  cost_type: string;
  amount: number;
  cost_date: string;
  description: string | null;
}

const VEHICLE_COST_TYPE_OPTIONS = [
  { value: "treibstoff", label: "Treibstoff" },
  { value: "strom", label: "Strom (Laden)" },
  { value: "waschstrasse", label: "Waschstraße" },
  { value: "reifenwechsel", label: "Reifenwechsel" },
  { value: "werkstatt", label: "Werkstattbesuch" },
  { value: "tuev", label: "TÜV" },
  { value: "reparatur", label: "Reparatur" },
  { value: "inspektion", label: "Inspektion" },
  { value: "sonstiges", label: "Sonstiges" },
];

export function EmployeeVehicleCostsTab({ employeeId, employeeName = "Mitarbeiter", readOnly = false }: EmployeeVehicleCostsTabProps) {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [assignedVehicleId, setAssignedVehicleId] = useState<string | null>(null);
  const [vehicleOneTimeCosts, setVehicleOneTimeCosts] = useState<VehicleOneTimeCost[]>([]);
  const [evHomeChargings, setEvHomeChargings] = useState<EVHomeCharging[]>([]);
  const [loading, setLoading] = useState(true);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [vehicleCostDialogOpen, setVehicleCostDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>("");
  const [vehicleCostFormData, setVehicleCostFormData] = useState({
    cost_type: "",
    amount: 0,
    cost_date: new Date().toISOString().split("T")[0],
    description: "",
  });

  useEffect(() => {
    if (user && employeeId) {
      loadData();
    }
  }, [user, employeeId]);

  const loadData = async () => {
    try {
      // Load all vehicles
      const { data: vehiclesData, error: vehiclesError } = await supabase
        .from("vehicles")
        .select("id, name, manufacturer, license_plate, leasing_rate, insurance_monthly, tax_yearly, fuel_monthly, responsible_employee_id")
        .order("name");

      if (vehiclesError) throw vehiclesError;
      setVehicles(vehiclesData || []);

      // Find vehicle assigned to this employee
      const assigned = vehiclesData?.find(v => v.responsible_employee_id === employeeId);
      setAssignedVehicleId(assigned?.id || null);

      // Load vehicle one-time costs for the assigned vehicle
      if (assigned) {
        const { data: oneTimeCostsData, error: oneTimeCostsError } = await supabase
          .from("vehicle_one_time_costs")
          .select("*")
          .eq("vehicle_id", assigned.id)
          .order("cost_date", { ascending: false });

        if (oneTimeCostsError) throw oneTimeCostsError;
        setVehicleOneTimeCosts(oneTimeCostsData || []);
      } else {
        setVehicleOneTimeCosts([]);
      }

      // Load EV home charging data
      const { data: evChargingData, error: evChargingError } = await supabase
        .from("ev_home_charging")
        .select("id, calculated_amount, is_settled")
        .eq("employee_id", employeeId);

      if (evChargingError) throw evChargingError;
      setEvHomeChargings(evChargingData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Fehler beim Laden der KFZ-Kosten");
    } finally {
      setLoading(false);
    }
  };

  const getCostTypeLabel = (costType: string) => {
    const option = VEHICLE_COST_TYPE_OPTIONS.find(o => o.value === costType);
    return option?.label || costType;
  };

  // Get assigned vehicle details
  const assignedVehicle = vehicles.find(v => v.id === assignedVehicleId);

  // Calculate vehicle monthly costs
  const getVehicleMonthlyCost = () => {
    if (!assignedVehicle) return 0;
    return (
      (assignedVehicle.leasing_rate || 0) +
      (assignedVehicle.insurance_monthly || 0) +
      ((assignedVehicle.tax_yearly || 0) / 12)
    );
  };

  const getVehicleOneTimeCostTotal = () => {
    return vehicleOneTimeCosts.reduce((sum, c) => sum + (c.amount || 0), 0);
  };

  const getFuelCostTotal = () => {
    return vehicleOneTimeCosts
      .filter(c => c.cost_type === "treibstoff")
      .reduce((sum, c) => sum + (c.amount || 0), 0);
  };

  const getChargingCostTotal = () => {
    return vehicleOneTimeCosts
      .filter(c => c.cost_type === "strom")
      .reduce((sum, c) => sum + (c.amount || 0), 0);
  };

  const getEVHomeChargingTotal = () => {
    return evHomeChargings.reduce((sum, c) => sum + (c.calculated_amount || 0), 0);
  };

  const getOtherOneTimeCostTotal = () => {
    return vehicleOneTimeCosts
      .filter(c => c.cost_type !== "treibstoff" && c.cost_type !== "strom")
      .reduce((sum, c) => sum + (c.amount || 0), 0);
  };

  const unassignedVehicles = vehicles.filter(v => !v.responsible_employee_id);

  // Vehicle assignment functions
  const handleAssignVehicle = async () => {
    if (!selectedVehicleId) {
      toast.error("Bitte wählen Sie ein Fahrzeug aus");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("vehicles")
        .update({ responsible_employee_id: employeeId })
        .eq("id", selectedVehicleId);

      if (error) throw error;

      toast.success("Fahrzeug erfolgreich zugewiesen");
      setVehicleDialogOpen(false);
      setSelectedVehicleId("");
      loadData();
    } catch (error) {
      console.error("Error assigning vehicle:", error);
      toast.error("Fehler beim Zuweisen");
    } finally {
      setSaving(false);
    }
  };

  const handleUnassignVehicle = async () => {
    if (!confirm("Möchten Sie die Zuweisung dieses Fahrzeugs wirklich aufheben?")) return;

    try {
      const { error } = await supabase
        .from("vehicles")
        .update({ responsible_employee_id: null })
        .eq("id", assignedVehicleId);

      if (error) throw error;

      toast.success("Zuweisung aufgehoben");
      loadData();
    } catch (error) {
      console.error("Error unassigning vehicle:", error);
      toast.error("Fehler beim Aufheben der Zuweisung");
    }
  };

  const handleAddVehicleCost = async () => {
    if (!vehicleCostFormData.cost_type || !vehicleCostFormData.amount || !assignedVehicleId) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("vehicle_one_time_costs").insert([{
        vehicle_id: assignedVehicleId,
        cost_type: vehicleCostFormData.cost_type,
        amount: vehicleCostFormData.amount,
        cost_date: vehicleCostFormData.cost_date,
        description: vehicleCostFormData.description || null,
        created_by: user!.id,
      }]);

      if (error) throw error;

      toast.success("Kosten erfolgreich hinzugefügt");
      setVehicleCostDialogOpen(false);
      setVehicleCostFormData({
        cost_type: "",
        amount: 0,
        cost_date: new Date().toISOString().split("T")[0],
        description: "",
      });
      loadData();
    } catch (error) {
      console.error("Error adding vehicle cost:", error);
      toast.error("Fehler beim Hinzufügen");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVehicleCost = async (id: string) => {
    if (!confirm("Möchten Sie diesen Eintrag wirklich löschen?")) return;

    try {
      const { error } = await supabase.from("vehicle_one_time_costs").delete().eq("id", id);

      if (error) throw error;
      toast.success("Eintrag gelöscht");
      loadData();
    } catch (error) {
      console.error("Error deleting vehicle cost:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const vehicleMonthlyCost = getVehicleMonthlyCost();
  const vehicleOneTimeCostTotal = getVehicleOneTimeCostTotal();
  const fuelCostTotal = getFuelCostTotal();
  const chargingCostTotal = getChargingCostTotal();
  const evHomeChargingTotal = getEVHomeChargingTotal();
  const otherOneTimeCostTotal = getOtherOneTimeCostTotal();

  return (
    <>
      {/* Kosten-Zusammenfassung */}
      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Monatliche KFZ-Kosten</p>
                <p className="text-2xl font-bold text-primary">
                  {vehicleMonthlyCost.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              {fuelCostTotal > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Tankkosten:</span>
                  <span className="font-medium text-orange-600">{fuelCostTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
                </div>
              )}
              {chargingCostTotal > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Ladekosten (Fahrzeug):</span>
                  <span className="font-medium text-green-600">{chargingCostTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
                </div>
              )}
              {evHomeChargingTotal > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Heimladen:</span>
                  <span className="font-medium text-emerald-600">{evHomeChargingTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
                </div>
              )}
              {otherOneTimeCostTotal > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Sonstige:</span>
                  <span className="font-medium">{otherOneTimeCostTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KFZ-Kosten Rubrik */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Car className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>KFZ-Kosten</CardTitle>
                <CardDescription>
                  Zugewiesenes Fahrzeug und dessen Kosten
                </CardDescription>
              </div>
            </div>
            {!readOnly && !assignedVehicleId && (
              <Button onClick={() => setVehicleDialogOpen(true)} disabled={unassignedVehicles.length === 0}>
                <Plus className="mr-2 h-4 w-4" />
                Fahrzeug zuweisen
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {assignedVehicle ? (
            <div className="space-y-4">
              {/* Vehicle Cost Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Gesamtkosten (alle)</div>
                  <div className="text-2xl font-bold text-primary">
                    {(() => {
                      const leasingYearly = (assignedVehicle.leasing_rate || 0) * 12;
                      const insuranceYearly = (assignedVehicle.insurance_monthly || 0) * 12;
                      const taxYearly = assignedVehicle.tax_yearly || 0;
                      const oneTimeTotal = vehicleOneTimeCostTotal;
                      return (leasingYearly + insuranceYearly + taxYearly + oneTimeTotal).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
                    })()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">inkl. einmalige Kosten</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Laufende Kosten/Jahr</div>
                  <div className="text-2xl font-bold">
                    {(() => {
                      const leasingYearly = (assignedVehicle.leasing_rate || 0) * 12;
                      const insuranceYearly = (assignedVehicle.insurance_monthly || 0) * 12;
                      const taxYearly = assignedVehicle.tax_yearly || 0;
                      return (leasingYearly + insuranceYearly + taxYearly).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
                    })()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Leasing + Versicherung + Steuer</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Ø Kosten/Monat</div>
                  <div className="text-2xl font-bold">
                    {(() => {
                      const leasingYearly = (assignedVehicle.leasing_rate || 0) * 12;
                      const insuranceYearly = (assignedVehicle.insurance_monthly || 0) * 12;
                      const taxYearly = assignedVehicle.tax_yearly || 0;
                      const oneTimeTotal = vehicleOneTimeCostTotal;
                      const totalYearly = leasingYearly + insuranceYearly + taxYearly + oneTimeTotal;
                      return (totalYearly / 12).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
                    })()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Gesamtkosten ÷ 12</div>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="flex items-center justify-between flex-wrap gap-4 p-4 border rounded-lg bg-muted/50">
                <div className="flex items-center gap-4">
                  <Car className="h-10 w-10 text-muted-foreground" />
                  <div>
                    <h4 className="font-semibold">{assignedVehicle.name}</h4>
                    <div className="text-sm text-muted-foreground">
                      {assignedVehicle.manufacturer && <span>{assignedVehicle.manufacturer} • </span>}
                      {assignedVehicle.license_plate && <Badge variant="outline">{assignedVehicle.license_plate}</Badge>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Monatlich (laufend)</div>
                    <div className="text-xl font-bold text-primary">
                      {vehicleMonthlyCost.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </div>
                  </div>
                  {!readOnly && (
                    <Button variant="ghost" size="icon" onClick={handleUnassignVehicle}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Cost Breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {assignedVehicle.leasing_rate > 0 && (
                  <div className="text-center p-3 border rounded-lg">
                    <div className="font-semibold">
                      {assignedVehicle.leasing_rate.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </div>
                    <div className="text-xs text-muted-foreground">Leasing (mtl.)</div>
                  </div>
                )}
                {assignedVehicle.insurance_monthly > 0 && (
                  <div className="text-center p-3 border rounded-lg">
                    <div className="font-semibold">
                      {assignedVehicle.insurance_monthly.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </div>
                    <div className="text-xs text-muted-foreground">Versicherung (mtl.)</div>
                  </div>
                )}
                {assignedVehicle.tax_yearly > 0 && (
                  <div className="text-center p-3 border rounded-lg">
                    <div className="font-semibold">
                      {assignedVehicle.tax_yearly.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </div>
                    <div className="text-xs text-muted-foreground">Steuer (jährl.)</div>
                  </div>
                )}
                {vehicleOneTimeCostTotal > 0 && (
                  <div className="text-center p-3 border rounded-lg bg-amber-500/10">
                    <div className="font-semibold text-amber-600">
                      {vehicleOneTimeCostTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </div>
                    <div className="text-xs text-muted-foreground">Einmalige Kosten</div>
                  </div>
                )}
              </div>

              {/* One-Time Costs */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    Einmalige Kosten
                    {vehicleOneTimeCostTotal > 0 && (
                      <Badge variant="secondary">
                        {vehicleOneTimeCostTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                      </Badge>
                    )}
                  </h4>
                  {!readOnly && (
                    <Button variant="outline" size="sm" onClick={() => setVehicleCostDialogOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Kosten hinzufügen
                    </Button>
                  )}
                </div>

                {vehicleOneTimeCosts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Datum</TableHead>
                          <TableHead>Art</TableHead>
                          <TableHead>Betrag</TableHead>
                          <TableHead>Beschreibung</TableHead>
                          {!readOnly && <TableHead></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vehicleOneTimeCosts.map((cost) => (
                          <TableRow key={cost.id}>
                            <TableCell>
                              {format(new Date(cost.cost_date), "dd.MM.yyyy", { locale: de })}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{getCostTypeLabel(cost.cost_type)}</Badge>
                            </TableCell>
                            <TableCell className="font-medium">
                              {cost.amount.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate">
                              {cost.description || "-"}
                            </TableCell>
                            {!readOnly && (
                              <TableCell>
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteVehicleCost(cost.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">Keine einmaligen Kosten vorhanden</p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
              <p className="text-muted-foreground">
                Kein Fahrzeug zugewiesen. 
                {unassignedVehicles.length > 0 
                  ? " Weisen Sie ein Fahrzeug aus dem Pool zu."
                  : " Alle Fahrzeuge sind bereits zugewiesen."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Fahrzeug zuweisen */}
      <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fahrzeug zuweisen</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Fahrzeug auswählen *</Label>
              <Select value={selectedVehicleId} onValueChange={setSelectedVehicleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Fahrzeug wählen" />
                </SelectTrigger>
                <SelectContent>
                  {unassignedVehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                      {vehicle.license_plate && ` (${vehicle.license_plate})`}
                      {vehicle.manufacturer && ` - ${vehicle.manufacturer}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedVehicleId && (() => {
              const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId);
              if (!selectedVehicle) return null;
              const monthlyCost = (selectedVehicle.leasing_rate || 0) + 
                (selectedVehicle.insurance_monthly || 0) + 
                ((selectedVehicle.tax_yearly || 0) / 12);
              return (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="font-medium">{selectedVehicle.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Monatliche Kosten: {monthlyCost.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                  </p>
                </div>
              );
            })()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setVehicleDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleAssignVehicle} disabled={saving || !selectedVehicleId}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Zuweisen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Fahrzeugkosten hinzufügen */}
      <Dialog open={vehicleCostDialogOpen} onOpenChange={setVehicleCostDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fahrzeugkosten hinzufügen</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Kostenart *</Label>
              <Select 
                value={vehicleCostFormData.cost_type} 
                onValueChange={(value) => setVehicleCostFormData({ ...vehicleCostFormData, cost_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kostenart wählen" />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_COST_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Betrag (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={vehicleCostFormData.amount || ""}
                  onChange={(e) => setVehicleCostFormData({ ...vehicleCostFormData, amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={vehicleCostFormData.cost_date}
                  onChange={(e) => setVehicleCostFormData({ ...vehicleCostFormData, cost_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Beschreibung</Label>
              <Textarea
                rows={2}
                value={vehicleCostFormData.description}
                onChange={(e) => setVehicleCostFormData({ ...vehicleCostFormData, description: e.target.value })}
                placeholder="z.B. Tankstelle, Ladestation, etc."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setVehicleCostDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleAddVehicleCost} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* E-Mobilität Ladekosten */}
      <EVChargingCostsTab 
        employeeId={employeeId} 
        employeeName={employeeName}
        readOnly={readOnly}
      />
    </>
  );
}
